import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ActivityLogActions from '@states/activity-log/activity-log.actions';
import { withLatestFrom } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { catchError, debounceTime, exhaustMap, of, share, switchMap } from 'rxjs';
import * as SharedActions from '@states/shared/shared.actions';
import { AppState } from '../app.state';
import { ActivityLogService } from '../../development/activity-log.service';
import { OrganizationService } from '../../development/organization.service';
import { ActivityLogFilterName } from '@enums/activity-log.enum';
import { UtilsV2Service } from '../../services/utils-v2.service';
import moment from 'moment';
import { UserSelectors } from '@states/user/user.selector-types';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ActivityLogEffect {
  public detailsValidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivityLogActions.getActivityLog),
      withLatestFrom(this.store$.pipe(select(state => state.activityLogState))),
      exhaustMap(([, { limit, isLastPage, lastActivityTimestamp, filters }]) => {
        if (!isLastPage) {
          const dateRangeFilter = this.utilsV2Service.dateRangeToServerRequest(filters.dateRange);

          const endTime = lastActivityTimestamp ?? dateRangeFilter.end;
          return this.activityLogService
            .get(limit, {
              start: moment(dateRangeFilter.start)
                .toString(),
              end: moment(endTime)
                .toString(),
              events: filters.events,
              authProviderIds: filters.users,
              orgIds: filters.orgs.map(item => item.orgId),
              supportOrgIds: filters.supportOrgIds,
            })
            .pipe(
              switchMap(res => {
                return [ActivityLogActions.getActivityLogSuccess({ activityLogs: res })];
              }),
              catchError(err => of(SharedActions.doNothing())),
            );
        } else {
          return of(SharedActions.doNothing());
        }
      }),
      share(),
    ),
  );

  public downloadCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivityLogActions.downloadActivityLogCsv),
      withLatestFrom(this.store$.pipe(select((state) => state.activityLogState))),
      exhaustMap(([, { filters }]) => {
        const dateRangeFilter = this.utilsV2Service.dateRangeToServerRequest(filters.dateRange);

        this.store$.dispatch(SharedActions.setIsLoading({ isLoading: true }));

        const filename = `activity-log-${moment()
          .format('YYYY-MM-DD-HH-mm-ss')}.csv`;

        return this.activityLogService
          .downloadCsv({
            start: moment(dateRangeFilter.start)
              .toString(),
            end: moment(dateRangeFilter.end)
              .toString(),
            events: filters.events,
            authProviderIds: filters.users,
            orgIds: filters.orgs.map((item) => item.orgId),
            supportOrgIds: filters.supportOrgIds,
          })
          .pipe(
            switchMap((blob) => {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              link.click();
              window.URL.revokeObjectURL(url);

              this.store$.dispatch(SharedActions.setIsLoading({ isLoading: false }));

              return of(SharedActions.showMessage({ success: this.translateService.instant('TOASTS.CSV_DOWNLOAD_STARTED') }));
            }),
            catchError(() => {
              this.store$.dispatch(SharedActions.setIsLoading({ isLoading: false }));

              return of(SharedActions.showMessage({ error: this.translateService.instant('TOASTS.CSV_DOWNLOAD_FAILED') }));
            }),
          );
      }),
      share(),
    ),
  );


  public filter = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ActivityLogActions.setDateRangeFilter,
        ActivityLogActions.setFilter,
        ActivityLogActions.removeFilter,
        ActivityLogActions.refreshDateFilter,
      ),
      debounceTime(400),
      switchMap(() => {
        return [
          ActivityLogActions.resetToInitialStateWithoutFilters(),
          ActivityLogActions.getActivityLog(),
        ];
      }),
      share(),
    ),
  );

  public ifOrgSelectorChangedByDev = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivityLogActions.setFilter),
      switchMap(({ filter }) => {
        if (filter === ActivityLogFilterName.orgs) {
          return [ActivityLogActions.getOrgUsers()];
        }
        return [
          SharedActions.doNothing(),
        ];
      }),
      share(),
    ),
  );

  public getOrgUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActivityLogActions.getOrgUsers),
      withLatestFrom(
        this.store$.pipe(select(state => state.activityLogState)),
        this.store$.pipe(select(UserSelectors.isDeveloper)),
        this.store$.pipe(select(UserSelectors.isDevTeam)),
      ),
      switchMap(() => {
        return this.organizationService.getActiveOrganizationMembers()
          .pipe(
            switchMap(users => {
              return of(ActivityLogActions.getOrgUsersSuccess({
                users: users.users,
                supportOrganizations: users.invitedOrganizations,
              }));
            }),
          );
        },
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private activityLogService: ActivityLogService,
    private organizationService: OrganizationService,
    private utilsV2Service: UtilsV2Service,
    private translateService: TranslateService,
  ) {}
}
